/* eslint-disable @typescript-eslint/no-explicit-any */

import React, {
  FC,
  FormEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Dialog } from 'primereact/dialog';
import { useTranslation } from 'react-i18next';
import { RadioButton } from 'primereact/radiobutton';
import { useMutation, useQuery } from '@tanstack/react-query';
import { InputNumberValueChangeEvent } from 'primereact/inputnumber';
import { Divider } from 'primereact/divider';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDollar } from '@fortawesome/free-solid-svg-icons';
import {
  Button,
  DropdownField,
  InputTextField,
  LoadingSpinner,
} from '@/components';
import { Banco, Depo20FormData, DocumentTableData } from '@/types/depo20';
import { useToastNotification, useAuthContext } from '@/hooks';
import { titularRut } from '@/pages/CustomerRegistration/titularRut';
import { parametroBooleano } from '@/services/parametroBooleano';
import { ImportFCLData } from '@/types/tableImportFCL';
import { PagesPaths } from '@/enums';
import styles from './ModalDetails.module.scss';

import { getBancos } from '../services/getBancos';
import { getBuscarDocumento } from '../services/getBuscarDocumento';
import { getDepo20TableList } from '../services/getDepo20TableList';
import { getVisitasReliquidadas } from '../services/getVisitasReliquidadas';
import { pagoSistarBanc } from '../services/pagoSistarBanc';
import { pagoWeb } from '../services/pagoWeb';
import { getTotalPreDocumento } from '../services/getTotalPredocumento';
import { getPagoDocumentoBooking } from '../services/pagoDocumentoBooking';
import { getPagoDocumentoLiquidacion } from '../services/pagoDocumentoLiquidacion';
import { comprobarDocumento } from '../services/comprobarDocumento';
import { tieneCredito as tieneCreditoApi } from '../services/tieneCredito';
import { tieneDiasCredito as tieneDiasCreditoApi } from '../services/tieneDiasCredito';
import { pagoFacturaWeb } from '../services/pagoFacturaWeb';
import { Depo20FormState } from '../Depo20ClientForm';
import { facturaUnicaPuedeProformar } from '../services/facturaUnicaPuedeProformar';
import { getVisitasLiquidadas } from '../services/getVisitasLiquidadas';
import {
  saveAndAddSettlement,
  SaveSettlementValidated,
} from '../services/saveAndAddSettlement';

type ModalDetailsProps = {
  oid: number;
  isDialogShown: boolean;
  toggleDialog: () => void;
  formClientData: Depo20FormData | undefined;
  fetchAddLiquidacionLoading: boolean;
  saveLiquidacion: () => void;
  formDataValues?: Depo20FormState;
  // importExportTableData?: ImportFCLData[];
  tipoLiquidacion?: string;
  importes?: ImportFCLData[];
  esProformaWebBody?: SaveSettlementValidated;
};

type PaymentOption = {
  key: string;
  value: string;
};

interface DocumentTypeFormState {
  documentType: string | null;
  documentNumber: string | null;
  bank: number;
}

interface DocumentTypeFormErrors {
  documentType: string;
  documentNumber: string;
  bank: string;
}

function obtenerErrores(elementos: any[]): string[] {
  return elementos
    .map((elemento) => {
      if (!elemento) return 'No se encuentra proforma con ese número';
      if (elemento.anulada)
        return `No se puede facturar la proforma N°${elemento.numero} por estar anulada.`;
      if (elemento.sustituida)
        return `No se puede facturar la proforma N°${elemento.numero} por estar sustituida.`;
      if (elemento.oidDocumentoFacturado !== 0)
        return `No se puede agregar la proforma N°${elemento.numero} a la liquidación.`;
      return '';
    })
    .filter((error) => error !== '');
}

function getDocumentType(action: string): string {
  switch (action) {
    case 'Exportación':
      return 'RECEPCION FULL EXPORT';
    case 'Importación':
      return 'ENTREGA FULL IMPORT';
    case 'VacíoImportación':
      return 'RECEPCION VACIO IMPORT';
    case 'DEPO20':
      return 'PROFORMA DEPO20';
    case 'Proforma':
      return 'PROFORMA LIBRE';
    default:
      return 'INVALID TYPE';
  }
}

const ModalDetailPayment: FC<ModalDetailsProps> = ({
  oid,
  isDialogShown,
  toggleDialog,
  formClientData,
  fetchAddLiquidacionLoading,
  saveLiquidacion,
  formDataValues,
  tipoLiquidacion,
  importes,
  esProformaWebBody,
}) => {
  const { showToast } = useToastNotification();
  const { userDataDecoded } = useAuthContext();
  const navigate = useNavigate();
  const baseUrl = new URL(window.location.origin).origin;
  localStorage.setItem('urlVuelta', `${baseUrl}${PagesPaths.mySettlement}`);

  const paymentUrl =
    process.env['REACT_APP_PAYMENT_URL'] ??
    'https://spf.quanam.com/spfe/servlet/PagoEmpresa';

  const { t } = useTranslation('depo20');
  const [paymentOption, setPaymentOption] = useState<PaymentOption[]>([
    { key: 'pagoOnline', value: t('payOnlineOption') },
  ]);

  useQuery<any>({
    queryKey: ['facturaUnicaPuedeProformar', oid],
    queryFn: () =>
      facturaUnicaPuedeProformar({
        titularesMagma: userDataDecoded?.facwebcc,
        rutCliente: formClientData?.rutCliente,
      }).then((response) => {
        if (response && userDataDecoded?.facwebcc) {
          const exists = paymentOption.some(
            (option) => option.key === 'facturaUnica'
          );

          if (!exists && tipoLiquidacion === 'Exportación') {
            setPaymentOption([
              ...paymentOption,
              { key: 'facturaUnica', value: t('singleInvoiceOption') },
            ]);
          }
          return paymentOption;
        }
        return null;
      }),
  });
  const [confirmButtonPress, setConfirmButtonPress] = useState<boolean>(false);

  const [selectedPayment, setSelectedPayment] = useState<string | null>(null);
  const [formState, setFormState] = useState<DocumentTypeFormState>({
    documentType: 'RUT',
    documentNumber: null,
    bank: 0,
  });

  const [formErrors, setFormErrors] = useState<DocumentTypeFormErrors>({
    documentType: '',
    documentNumber: '',
    bank: '',
  });

  const [puedeFacturarWeb, setPuedeFacturarWeb] = useState<boolean>(false);

  const { data: comboBancos } = useQuery({
    queryKey: ['comboBancoListDropdown'],
    queryFn: getBancos,
  });

  const selectedBank: Banco | undefined = comboBancos?.find(
    (val) => val.oid === formState.bank
  );

  const sumImporte = (data: any[]): number => {
    return data?.reduce((sum, item) => {
      return item.importeSpecified ? sum + item.importe : sum;
    }, 0);
  };

  const formRef = useRef<HTMLFormElement>(null);

  const createAndSubmitForm = (data: any) => {
    const form = formRef.current;
    if (form) {
      while (form.firstChild) {
        form.removeChild(form.firstChild);
      }

      Object.keys(data).forEach((key) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = data[key];
        form.appendChild(input);
      });

      form.submit();
    }
  };

  const { mutate: getPagoFacturaWeb, isPending: getPagoFacturaWebLoading } =
    useMutation({
      mutationKey: ['pagoFacturaWeb', oid],
      mutationFn: () =>
        pagoFacturaWeb({
          oidLiquidacion: oid,
          rut: formDataValues?.rut || formClientData?.rutCliente,
        }),
      onSuccess: (response) => {
        if (response.ok) {
          showToast({
            severity: 'success',
            detail:
              response.mensaje ??
              'Estamos procesando su solicitud de facturación. En breve quedarán disponibles comprobantes fiscales electrónicos, documentación y habilitación de servicios.',
          });
        }
        if (!response.ok) {
          showToast({
            severity: 'error',
            detail: response.mensaje ?? t('unexpectedError'),
          });
        }
      },
      onError: () => {
        showToast({
          severity: 'error',
          detail: t('unexpectedError'),
        });
      },
      onSettled: () => {
        toggleDialog();
        navigate(PagesPaths.mySettlement);
      },
    });

  const { mutate: saveData, isPending: savingAndAddSettlement } = useMutation({
    mutationFn: saveAndAddSettlement,
    onSuccess: () => {
      getPagoFacturaWeb();
    },
    onError: () => {
      showToast({
        severity: 'error',
        detail: t('settlementNotSaved'),
      });
    },
  });

  const {
    mutateAsync: totalDocumentoBooking,
    isPending: getTotalDocumentoBookingLoading,
  } = useMutation({
    mutationKey: ['getTotalDocumentoBooking', oid],
    mutationFn: () => getPagoDocumentoBooking(oid),
  });

  const {
    mutateAsync: totalPagoDocumentoLiquidacion,
    isPending: getTotalPagoDocumentoLiquidacionLoading,
  } = useMutation({
    mutationKey: ['getTotalPagoDocumentoLiquidacion', oid],
    mutationFn: () => getPagoDocumentoLiquidacion(oid),
  });

  const {
    mutateAsync: totalPredocumento,
    isPending: getTotalPredocumentoLoading,
  } = useMutation({
    mutationKey: ['getTotalPredocumento', oid],
    mutationFn: () => getTotalPreDocumento(oid),
  });

  const { mutateAsync: getVisitasLiquidadasAsync, isPending: scheduling } =
    useMutation({
      mutationFn: getVisitasLiquidadas,
    });

  const buildImporte = async () => {
    let importe = 0;
    if (
      formClientData?.tipo === 'DEPO20' ||
      formClientData?.tipo === 'Proforma'
    ) {
      try {
        const result = await totalPredocumento();
        importe = result ?? 0;
      } catch (error) {
        importe = 0;
      }
    }

    if (formClientData?.tipo === 'DocumentoCredito') {
      try {
        const result = await totalPagoDocumentoLiquidacion();
        importe = sumImporte(result ?? []);
      } catch (error) {
        importe = 0;
      }
    }

    if (
      formClientData?.tipo === 'Exportación' ||
      formClientData?.tipo === 'Importación' ||
      formClientData?.tipo === 'VacíoImportación'
    ) {
      try {
        const result = await getVisitasLiquidadasAsync(oid);
        importe = sumImporte(result);
      } catch (error) {
        importe = 0;
      }
    }

    return Math.round(importe * 100).toString();
  };

  const { mutate: getPagoSistarBanc, isPending: getPagosSistarBancPending } =
    useMutation({
      mutationKey: ['pagoSistarBank', oid],
      mutationFn: () =>
        pagoSistarBanc({
          oidLiquidacion: oid,
          oidBanco: selectedBank?.oid,
          documentoParaPago: Number(formState.documentNumber),
        }),

      onSuccess: async (response) => {
        if (response.ok) {
          const formData = {
            idBanco: process.env['REACT_APP_BASE_URL']?.includes('testing')
              ? '009'
              : selectedBank?.codigoBCU,
            idTransaccion: response.guidMensaje,
            idOrganismo: 'MONTECON',
            tipoServicio: 'SERV',
            idCuenta: formState.documentNumber,
            idFactura: oid,
            importe: await buildImporte(),
            moneda: 'USD',
            otroDato: formDataValues?.referencia ?? '',
            urlVuelta: `${localStorage.getItem('urlVuelta')}`,
          };

          createAndSubmitForm(formData);
        } else {
          showToast({
            severity: 'error',
            detail: t('cannotPaySettlement'),
          });
        }
      },
      onSettled: () => {
        toggleDialog();
        navigate(PagesPaths.mySettlement);
      },
    });

  const { mutate: getPagoWeb, isPending: getPagosWeb } = useMutation({
    mutationKey: ['pagoWeb', oid],
    mutationFn: () =>
      pagoWeb({
        oidLiquidacion: oid,
        oidBanco: selectedBank?.oid,
        documento: `${formState.documentNumber}`,
        tipoDocumento: formState.documentType ?? 'RUT',
      }),
    onSuccess: (response) => {
      if (!response.ok) {
        showToast({
          severity: 'error',
          detail: response.mensaje,
        });
      }
      if (response.ok) {
        showToast({
          severity: 'success',
          detail: response.mensaje ?? '',
        });
      }
    },
    onSettled: () => {
      toggleDialog();
      navigate(PagesPaths.mySettlement);
    },
  });

  const {
    mutateAsync: getVisitasLiquidacion,
    isPending: getVisitasLiquidacionLoading,
  } = useMutation({
    mutationKey: ['VisitasReliquidadas', oid],
    mutationFn: () => getVisitasReliquidadas(oid),
  });

  const {
    mutateAsync: getParametroBooleano,
    isPending: getParametroBooleanoLoading,
  } = useMutation({
    mutationKey: ['parametroBooleano', oid],
    mutationFn: parametroBooleano,
  });

  const { mutateAsync: getTieneCredito, isPending: tieneCreditoLoading } =
    useMutation({
      mutationKey: ['tieneCredito', oid],
      mutationFn: tieneCreditoApi,
    });

  const {
    mutateAsync: getTieneDiasCredito,
    isPending: tieneDiasCreditoLoading,
  } = useMutation({
    mutationKey: ['tieneDiasCredito', oid],
    mutationFn: tieneDiasCreditoApi,
  });

  function isNullOrEmpty(str: string | null | undefined): boolean {
    return str == null || str.trim() === '';
  }

  const puedeFacturarCreditoWeb = useCallback(async () => {
    // Usar el RUT del formulario si está disponible, sino usar el de formClientData
    const rutCliente = formDataValues?.rut || formClientData?.rutCliente;

    const habilitado = await getParametroBooleano({
      codigo: 'HABILITAR FACTURAS CREDITO WEB',
    });
    const titMagma = userDataDecoded?.facwebcc;
    const titMagmaValid = !isNullOrEmpty(titMagma);
    const tieneCredito = await getTieneCredito({
      rut: rutCliente,
    });

    let canFacturarWeb: boolean;

    if (tipoLiquidacion === 'DEPO20' || tipoLiquidacion === 'Proforma') {
      const diasCreditoExpoImpo = [
        getTieneDiasCredito({
          rut: rutCliente,
          clasificacionDocumento: getDocumentType(tipoLiquidacion),
        }).then((result) => ({
          result,
        })),
      ];
      let res: any;
      await Promise.all(diasCreditoExpoImpo).then((response: any) => {
        res = response;
      });

      canFacturarWeb =
        titMagmaValid && habilitado.value && tieneCredito && res[0].result;

      setPuedeFacturarWeb(canFacturarWeb);
    }

    if (
      tipoLiquidacion === 'Exportación' ||
      tipoLiquidacion === 'Importación' ||
      tipoLiquidacion === 'VacíoImportación'
    ) {
      const diasCreditoExpoImpo = [
        getTieneDiasCredito({
          rut: rutCliente,
          clasificacionDocumento: getDocumentType(tipoLiquidacion),
        }).then((result) => ({
          result,
        })),
      ];
      let res: any;
      await Promise.all(diasCreditoExpoImpo).then((response) => {
        res = response;
      });

      canFacturarWeb =
        titMagmaValid && habilitado.value && tieneCredito && res[0].result;

      setPuedeFacturarWeb(canFacturarWeb);
    }
  }, [
    getParametroBooleano,
    userDataDecoded,
    formClientData?.rutCliente,
    formDataValues?.rut,
    getTieneDiasCredito,
    getTieneCredito,
    tipoLiquidacion,
  ]);

  useEffect(() => {
    if (puedeFacturarWeb) {
      const exists = paymentOption.some(
        (option) => option.key === 'facturaWeb'
      );
      if (!exists) {
        setPaymentOption([
          ...paymentOption,
          { key: 'facturaWeb', value: t('webInvoiceOption') },
        ]);
      }
    }
  }, [paymentOption, puedeFacturarWeb, t]);

  useEffect(() => {
    puedeFacturarCreditoWeb();
  }, [puedeFacturarCreditoWeb]);

  const {
    mutate: getSeleccionadosEnLiquidacion,
    isPending: getSeleccionadosEnLiquidacionLoading,
  } = useMutation({
    mutationKey: ['GetSeleccionadosBankLiqui', oid],
    mutationFn: () => getDepo20TableList(oid),
    onSuccess: async (response: DocumentTableData[]) => {
      await getVisitasLiquidacion();
      const results = importes ?? response;
      const totalAmount = results.reduce((sum, item) => sum + item.importe, 0);
      const errores = obtenerErrores(response);

      const showMessageErrorAmountIs0 = () =>
        showToast({
          severity: 'error',
          detail: t('cannotPaySettlement'),
        });

      if (
        selectedPayment === 'facturaWeb' ||
        selectedPayment === 'facturaUnica'
      ) {
        if (totalAmount <= 0) {
          showMessageErrorAmountIs0();
        }

        await puedeFacturarCreditoWeb();

        if (
          errores.length > 0 &&
          (tipoLiquidacion === 'DEPO20' || tipoLiquidacion === 'Proforma')
        ) {
          errores.forEach((error) =>
            showToast({
              severity: 'error',
              detail: error,
            })
          );
        }
        if (
          errores.length === 0 &&
          (tipoLiquidacion === 'DEPO20' || tipoLiquidacion === 'Proforma') &&
          puedeFacturarWeb &&
          selectedPayment === 'facturaWeb'
        ) {
          getPagoFacturaWeb();
        }

        if (
          (tipoLiquidacion === 'Exportación' ||
            tipoLiquidacion === 'Importación' ||
            tipoLiquidacion === 'VacíoImportación') &&
          puedeFacturarWeb &&
          (selectedPayment === 'facturaUnica' ||
            selectedPayment === 'facturaWeb')
        ) {
          if (esProformaWebBody) {
            saveData({
              ...esProformaWebBody,
              esProformaWeb: selectedPayment === 'facturaUnica',
            });
          }
        }
      }

      if (selectedPayment === 'pagoOnline') {
        if (totalAmount <= 0) {
          showMessageErrorAmountIs0();
        }
        if (errores.length > 0) {
          errores.forEach((error) =>
            showToast({
              severity: 'error',
              detail: error,
            })
          );
        }

        if (
          (formState.documentType === 'RUT' &&
            selectedBank?.intermediarioPagosElectronicos === 'BEVSA') ||
          (formState.documentType === 'CI' &&
            selectedBank?.intermediarioPagosElectronicosCI === 'BEVSA')
        ) {
          getPagoWeb();
        } else {
          getPagoSistarBanc();
        }
      }
    },
  });

  const { mutate: fetchBuscarDocumento, isPending: buscarDocLoading } =
    useMutation({
      mutationKey: ['BuscarDocumento', oid],
      mutationFn: () => getBuscarDocumento(oid),
      onSuccess: () => {
        if (
          formClientData?.tipo === 'DEPO20' ||
          formClientData?.tipo === 'Proforma'
        ) {
          getSeleccionadosEnLiquidacion();
        }
        if (
          formClientData?.tipo === 'Exportación' ||
          formClientData?.tipo === 'Importación' ||
          formClientData?.tipo === 'VacíoImportación'
        ) {
          getSeleccionadosEnLiquidacion();
        }
      },
    });

  const { mutate: mutateTitularRut, isPending: rutTitularLoading } =
    useMutation({
      mutationFn: titularRut,
      onSuccess: (response) => {
        if (!Object.keys(response).length) {
          showToast({
            severity: 'error',
            detail: t('titularDoesntExiste'),
          });
        }
        if (!userDataDecoded?.facwebcc && selectedPayment === 'facturaWeb') {
          showToast({
            severity: 'error',
            detail:
              'El RUT ingresado no está entre sus cuentas corrientes autorizadas.',
          });
        }

        if (userDataDecoded?.facwebcc && selectedPayment === 'facturaWeb') {
          const isRutAllowed = userDataDecoded?.facwebcc.includes(
            (formDataValues?.rut || formClientData?.rutCliente) ?? ''
          );

          if (!isRutAllowed) {
            showToast({
              severity: 'error',
              detail:
                'El RUT ingresado no está entre sus cuentas corrientes autorizadas.',
            });
          }
          if (isRutAllowed) {
            getSeleccionadosEnLiquidacion();
          }
        }
        if (selectedPayment === 'facturaUnica') {
          getSeleccionadosEnLiquidacion();
        }
      },
    });

  const { mutate: fetchComprobarDocumento, isPending: comprobarDocLoading } =
    useMutation({
      mutationKey: ['ComprobarDocumento', oid],
      mutationFn: () =>
        comprobarDocumento({
          documento: formDataValues?.rut || formClientData?.rutCliente,
          tipo: 2,
        }),
      onSuccess: (response) => {
        if (response === 1) {
          showToast({
            severity: 'error',
            detail:
              'El RUT ingresado pertenece a un titular sin dirección. En el proceso de factura electrónica es necesario que el titular contenga el dato mencionado. Comuníquese con atención al cliente para regularizar la información',
          });
        }
        if (response === 2) {
          showToast({
            severity: 'error',
            detail: 'El RUT ingresado es inválido',
          });
        } else {
          mutateTitularRut({
            rut: `${formDataValues?.rut || formClientData?.rutCliente}`,
          });
        }
      },
    });

  const dialogHeader = (
    <div className={`${styles.header} text-blue-800`}>
      <FontAwesomeIcon
        icon={faDollar}
        style={{ marginRight: '0.5rem' }}
        className="mr-3"
      />
      {t('paymentTitle')}
    </div>
  );

  const handleClientSelectionChange = (key: string) => {
    setSelectedPayment(key);
  };

  const documentTypes = [
    { name: 'Cédula de Identidad', value: 'CI' },
    { name: 'RUT', value: 'RUT' },
  ];

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement> | InputNumberValueChangeEvent,
    name: keyof DocumentTypeFormState
  ) => {
    const { value } = e.target;
    setFormState((prevState) => ({
      ...prevState,
      [name]: value,
    }));

    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [name]: '',
    }));
  };

  const validateForm = () => {
    let errors: DocumentTypeFormErrors = { ...formErrors };

    if (!formState.documentNumber) {
      errors = {
        ...errors,
        documentNumber: t('documentValidation'),
      };
    }

    setFormErrors(errors);
    return Object.values(errors).every((x) => x === '');
  };

  const handleDropdownChange = (
    e: { value: string | null },
    name: keyof DocumentTypeFormState
  ) => {
    setFormState((prevState) => ({
      ...prevState,
      [name]: e.value,
    }));
  };

  const validateRut = useCallback((): void => {
    const errors = { ...formErrors };

    if (formState.documentType === 'RUT') {
      if (!formState.documentNumber) {
        errors.documentNumber = t('paymentModalRutRequired');
      } else if (!/^\d+$/.test(formState.documentNumber)) {
        errors.documentNumber = t('paymentModalRutNumbers');
      } else {
        errors.documentNumber = '';
      }
    } else if (formState.documentType === 'CI') {
      if (!formState.documentNumber) {
        errors.documentNumber = t('ciRequired');
      } else if (!/^\d{7,8}$/.test(formState.documentNumber)) {
        errors.documentNumber = t('ciNumbers');
      } else {
        errors.documentNumber = '';
      }
    }

    setFormErrors(errors);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState.documentType, formState.documentNumber, t]);

  useEffect(() => {
    validateRut();
  }, [formState.documentNumber, formState.documentType, validateRut]);

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    validateForm();
    validateRut();

    if (
      selectedPayment === 'facturaWeb' ||
      (selectedPayment === 'facturaUnica' && formClientData)
    ) {
      fetchComprobarDocumento();
    }

    if (
      (selectedPayment === 'facturaWeb' ||
        selectedPayment === 'facturaUnica') &&
      !formClientData
    ) {
      saveLiquidacion();
    }

    if (validateForm()) {
      if (
        selectedPayment === 'pagoOnline' &&
        formState.documentType === 'RUT'
      ) {
        getSeleccionadosEnLiquidacion();
      }

      if (selectedPayment === 'pagoOnline' && formState.documentType === 'CI') {
        fetchBuscarDocumento();
      }
      if (
        formClientData?.tipo === 'DEPO20' ||
        formClientData?.tipo === 'Proforma'
      ) {
        totalPredocumento();
      }

      if (formClientData?.tipo === 'DocumentoCredito') {
        totalPagoDocumentoLiquidacion();
      }

      if (formClientData?.tipo === 'VacíoExportación') {
        totalDocumentoBooking();
      }
    }
  };

  const validateShowDocumentAndTypeOrRut =
    (selectedBank?.intermediarioPagosElectronicos === 'BEVSA' ||
      selectedBank?.intermediarioPagosElectronicosCI === 'BEVSA') &&
    selectedBank.permitePagarConCI === true;

  const validateFormData =
    formState.documentType === 'RUT'
      ? 'onlyRutPaymentPlaceholder'
      : 'documentLabel';
  const documentPlaceholder = !validateShowDocumentAndTypeOrRut
    ? 'onlyRutPaymentPlaceholderNew'
    : validateFormData;

  const validateFormDataName =
    formState.documentType === 'RUT'
      ? 'documentPaymentModalInputField'
      : 'documentLabel';
  const documentLabel = !validateShowDocumentAndTypeOrRut
    ? 'documentPaymentModalInputField'
    : validateFormDataName;

  return (
    <Dialog
      header={dialogHeader}
      visible={isDialogShown}
      style={{ width: window.innerWidth <= 500 ? '95vw' : '480px' }}
      onHide={toggleDialog}
      modal
    >
      <form
        ref={formRef}
        action={paymentUrl}
        method="POST"
        style={{ opacity: 0, height: 0, width: 0 }}
        target="_self"
        id="PostForm"
        name="PostForm"
      />
      <div className="p-4">
        <form onSubmit={handleSubmit}>
          <ul className={`list-none p-0 m-0 ${styles.list}`}>
            <div className={styles.paymentMethod}>
              {t('paymentMethodLabel')}
            </div>
            <hr className={styles.topDivider} />

            {paymentOption.map((client) => (
              <div
                key={client.key}
                className={`${styles.clientList} ${styles.paymentModal} `}
              >
                <hr className={styles.topDivider} />

                <li className="flex justify-content-start  align-items-center">
                  <RadioButton
                    inputId={client.key}
                    name="favoriteClient"
                    value={client.key}
                    onChange={() => handleClientSelectionChange(client.key)}
                    checked={selectedPayment === client.key}
                  />
                  <label htmlFor={client.key} className="ml-4 text-dark">
                    {client.value}
                  </label>
                </li>
                <hr />
              </div>
            ))}
            {!confirmButtonPress &&
              (rutTitularLoading ||
                buscarDocLoading ||
                getSeleccionadosEnLiquidacionLoading ||
                getVisitasLiquidacionLoading ||
                getPagosWeb ||
                getPagosSistarBancPending ||
                getTotalPredocumentoLoading ||
                getTotalPagoDocumentoLiquidacionLoading ||
                getTotalDocumentoBookingLoading ||
                comprobarDocLoading ||
                fetchAddLiquidacionLoading ||
                getParametroBooleanoLoading ||
                tieneCreditoLoading ||
                tieneDiasCreditoLoading ||
                getPagoFacturaWebLoading ||
                savingAndAddSettlement ||
                scheduling) && (
                <LoadingSpinner className="flex justify-content-center align-items-center my-5" />
              )}
          </ul>
          <Divider className={styles.paymentDivider} />

          {selectedPayment === 'pagoOnline' && (
            <>
              <DropdownField
                label={t('bankLabel')}
                options={comboBancos?.map((banco: Banco) => ({
                  name: banco.nombre,
                  value: banco.oid,
                }))}
                value={formState.bank}
                onChange={(e) => handleDropdownChange(e, 'bank')}
                placeholder={t('bankPlaceholder')}
                className="mb-4"
              />
              {validateShowDocumentAndTypeOrRut && (
                <DropdownField
                  label={t('documentTypeLabel')}
                  options={documentTypes}
                  value={formState.documentType}
                  onChange={(e) => handleDropdownChange(e, 'documentType')}
                  placeholder={t('selectDocumentTypePlaceholder')}
                  className="mb-4"
                />
              )}
              <InputTextField
                id="documentNumber"
                name="documentNumber"
                value={formState.documentNumber ?? ''}
                onChange={(e) => handleInputChange(e, 'documentNumber')}
                placeholder={t(documentPlaceholder)}
                error={formErrors.documentNumber}
                label={t(documentLabel)}
              />
              <div className="flex justify-content-end text-grey font-12">
                {t('redirectionNotice')}
              </div>
            </>
          )}
          <div className="flex justify-content-end mt-4">
            <button
              type="button"
              className={`${styles.cancelButton} mr-4`}
              onClick={toggleDialog}
            >
              {t('cancelButton')}
            </button>
            <Button
              className={`${styles.selectButton}`}
              type="submit"
              loading={
                confirmButtonPress &&
                (rutTitularLoading ||
                  buscarDocLoading ||
                  getSeleccionadosEnLiquidacionLoading ||
                  getVisitasLiquidacionLoading ||
                  getPagosWeb ||
                  getPagosSistarBancPending ||
                  getTotalPredocumentoLoading ||
                  getTotalPagoDocumentoLiquidacionLoading ||
                  getTotalDocumentoBookingLoading ||
                  comprobarDocLoading ||
                  fetchAddLiquidacionLoading ||
                  getParametroBooleanoLoading ||
                  tieneCreditoLoading ||
                  tieneDiasCreditoLoading ||
                  getPagoFacturaWebLoading ||
                  savingAndAddSettlement ||
                  scheduling)
              }
              onClick={() => setConfirmButtonPress(true)}
            >
              {t('confirmButton')}
            </Button>
          </div>
        </form>
      </div>
    </Dialog>
  );
};

export default ModalDetailPayment;
